/**
 * Web feature contract (ADR-0002 section 2, ADR-0005). A lane adds a feature by creating
 * apps/web/src/features/<id>/feature.ts whose default export is a manifest:
 *
 *   import { defineFeature } from "../../app/feature";
 *   import { MappingStudioPage } from "./MappingStudioPage";
 *
 *   export default defineFeature({
 *     id: "mapping-studio",                       // must equal the folder name
 *     title: "Mapping studio",
 *     routes: [{ path: "/mapping-studio", Component: MappingStudioPage }],
 *     nav: { label: "Mappings", path: "/mapping-studio", section: "Mappings" },
 *     permissions: ["mapping.propose"],           // any of; omit to show to every user
 *   });
 *
 * The app shell collects manifests with import.meta.glob; nobody edits apps/web/src/app.
 */
import type { ComponentType } from "react";

export const FEATURE_CONTRACT_VERSION = "1.0";

/** Primary navigation sections in DESIGN-SPEC section 28.1 order. */
export const NAV_SECTIONS = [
  "Engagements",
  "Corpus Runs",
  "Sources",
  "Observations",
  "Claims",
  "Mappings",
  "Evidence Context",
  "Review Queue",
  "Learning and Rules",
  "Outputs and Releases",
  "Knowledge Query",
  "Environment Profiles",
  "Administration",
] as const;

export type NavSection = (typeof NAV_SECTIONS)[number];

export interface FeatureRoute {
  /** Absolute path; must be /<id> or start with /<id>/. */
  path: string;
  Component: ComponentType;
}

export interface FeatureNavEntry {
  label: string;
  /** One of the feature's route paths. */
  path: string;
  section: NavSection;
  /** Order within the section (lower first). */
  order?: number;
}

export interface FeatureManifest {
  id: string;
  title: string;
  routes: FeatureRoute[];
  nav?: FeatureNavEntry;
  /** The user needs at least one of these permissions; omit for every signed-in user. */
  permissions?: string[];
}

export function defineFeature(manifest: FeatureManifest): FeatureManifest {
  return manifest;
}
